import BookingPageWrap from '@layouts/BookingPageWrap';
import {
  cortexPublicApi,
  CortexTreeNode,
  CortexTreeService,
  fetchServiceTree,
  fetchStorebrandServiceTree,
} from '@packages/cortex-sdk';
import { V1PatientPublicGetServices200ResponseInner } from '@packages/cortex-sdk/cortex-sdk/generated-client';
import {
  getVertical,
  useNavigationStore,
  useServiceTreeStore,
} from '@packages/shared';
import { Locales, Service, ServiceTree, Vertical } from '@packages/types';
import { ModalProvider } from '@packages/ui-web';
import { dehydrate, QueryClient, useQuery } from '@tanstack/react-query';
import Steps from 'layouts/BookingSteps';
import { GetStaticProps } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

const organizationId = process.env.NEXT_PUBLIC_ORGANIZATION_ID!;

type PartnerContext = { partnerId?: string; partner?: string };

/** Map cortex service tree response to our ServiceTree type */
function mapServiceTree(
  cortexTree: CortexTreeNode[],
  partner: PartnerContext,
): ServiceTree[] {
  return cortexTree.map((node) => ({
    id: node.id,
    shortId: node.shortId,
    name: node.name as ServiceTree['name'],
    order: node.order,
    description: node.description as ServiceTree['description'],
    services: (node.services ?? []).map((s) => mapTreeService(s, partner)),
    links: (node.links ?? []).map((l) => ({
      title: l.title as ServiceTree['name'],
      description: l.description as ServiceTree['name'],
      sortOrder: l.sortOrder,
      additionalInfo: l.additionalInfo as ServiceTree['name'],
      urls: l.urls as ServiceTree['name'],
      defaultUrl: l.defaultUrl,
    })),
    subCategories: mapServiceTree(node.subCategories ?? [], partner),
  }));
}

/**
 * Build a partial Service from a tree entry. Price reflects any partner
 * discount; metadata missing from the tree (clinicIds, surcharges, etc.) gets
 * filled in from the flat /services response in the store.
 */
function mapTreeService(
  s: CortexTreeService,
  partner: PartnerContext,
): Service {
  const discount = s.bestDiscount;
  const price = discount ? discount.discountedPrice : s.price;
  return {
    id: s.id,
    shortId: s.shortId,
    sortOrder: s.sortOrder,
    displayName: s.name as Service['displayName'],
    description: s.description as Service['description'],
    price,
    duration: s.duration,
    freeCancellationLimit: s.freeCancellationLimit,
    requiresPrepayment: s.requiresPrepayment,
    requiresBookWithPractitioner: s.requiresBookWithPractitioner,
    bookFirstAvailable: s.bookFirstAvailable,
    isVideo: s.isVideo,
    ...(discount
      ? {
          membershipCampaignId: discount.partnerCampaignId,
          partnerId: partner.partnerId,
          partnerName: partner.partner,
        }
      : {}),
  };
}

/** Map cortex services response to our Service type */
function mapServices(
  cortexServices: V1PatientPublicGetServices200ResponseInner[],
): Service[] {
  return cortexServices.map((s) => ({
    id: s.id,
    shortId: s.shortId,
    name: s.name,
    displayName: s.displayName as Service['displayName'],
    duration: s.duration,
    freeCancellationLimit: s.freeCancellationLimit,
    price: s.price,
    eveningSurcharge: s.eveningSurcharge,
    weekendSurcharge: s.weekendSurcharge,
    clinicIds: s.clinicIds,
    isVideo: s.isVideo,
    serviceCategoryId: s.serviceCategoryId,
    departmentId: s.departmentId,
    requiresPrepayment: s.requiresPrepayment,
    requiresBookWithPractitioner: s.requiresBookWithPractitioner,
    bookFirstAvailable: s.bookFirstAvailable,
  }));
}

async function fetchServiceData(partner: PartnerContext = {}) {
  const treePromise =
    partner.partner === 'storebrand'
      ? fetchStorebrandServiceTree(organizationId)
      : fetchServiceTree(organizationId, partner.partnerId);
  const [treeData, servicesResponse] = await Promise.all([
    treePromise,
    cortexPublicApi.v1PatientPublicGetServices(organizationId),
  ]);
  return {
    tree: mapServiceTree(treeData, partner),
    services: mapServices(servicesResponse.data),
  };
}

export default function Page() {
  const router = useRouter();
  const setServiceData = useServiceTreeStore((state) => state.setServiceData);
  const setQueries = useNavigationStore((state) => state.setQueries);

  const partnerId =
    typeof router.query.partnerId === 'string'
      ? router.query.partnerId
      : undefined;
  const partner =
    typeof router.query.partner === 'string' ? router.query.partner : undefined;

  useQuery(
    ['service-data', partnerId, partner],
    () => fetchServiceData({ partnerId, partner }),
    {
      enabled: router.isReady,
      onSuccess: (data) => {
        setServiceData(data);
        setQueries(router.query);
      },
      refetchOnWindowFocus: false,
    },
  );

  const { t } = useTranslation('booking');
  const vertical = getVertical(router.asPath);

  // Hack for iOS to set color of status bar
  // to be the same as the vertical theme currently selected.
  // Used in the meta content 'theme-color'
  const verticalColors: Record<Vertical, string> = {
    covid: '#2e4f4e',
    dermatologist: '#4d4557',
    'ear-nose-throat': '#4d4557',
    'eye-doctor': '#4d4557',
    general: '#2e4f4e',
    gynecologist: '#4d4557',
    'mental-health': '#b4561f',
    pediatrician: '#4d4557',
    physical: '#1d4865',
    psychology: '#b4561f',
    specialist: '#4d4557',
  };

  return (
    <>
      <Head>
        <title>{t('seo.title')}</title>
        <meta name="description" content={t('seo.meta_description')} />

        <meta name="theme-color" content={verticalColors[vertical]} />
      </Head>

      <ModalProvider>
        <BookingPageWrap>
          <Steps router={router} />
        </BookingPageWrap>
      </ModalProvider>
    </>
  );
}

type StaticProps = {
  locale: Locales;
} & GetStaticProps;

export async function getStaticProps({ locale }: StaticProps) {
  const queryClient = new QueryClient();
  await queryClient.prefetchQuery(['service-data'], () => fetchServiceData());
  return {
    props: {
      ...(await serverSideTranslations(locale, [
        'common',
        'booking',
        'partners',
      ])),
      dehydratedState: dehydrate(queryClient),
    },
    revalidate: 600,
  };
}
